import { CUSTOM_ELEMENTS_SCHEMA, Component, ViewChild, signal } from '@angular/core';
import { BillingServiceService } from '../billing-service.service';
import { ActivatedRoute } from '@angular/router';
import { Customer, CustomerMasterData, CustomerTaxCategory, Deal, DealPayment, DealType } from '../model';
import { StripePaymentElementComponent, injectStripe, StripeServiceInterface } from 'ngx-stripe';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { StripeElementLocale, StripeElementsOptions, StripePaymentElementOptions } from '@stripe/stripe-js';
import { environment } from '../../environments/environment';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { PanelModule } from 'primeng/panel';
import { DropdownModule } from 'primeng/dropdown';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FPAPipe } from '../pipes/fpa.pipe';
import { allCountries } from '../data/countries';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToolbarModule } from 'primeng/toolbar';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { LoadingService } from '../services/loading.service';

//https://stackblitz.com/edit/ngx-stripe-live?file=src%2Fmain.ts
//https://github.com/richnologies/ngx-stripe

@Component({
  selector: 'app-payment-gateway',
  standalone: true,
  providers: [ConfirmationService],
  imports: [
    TranslateModule,
    ReactiveFormsModule, CommonModule, StripePaymentElementComponent,
    ButtonModule, InputTextModule, TableModule, PanelModule, DropdownModule, ToolbarModule, CardModule, DialogModule, ConfirmDialogModule,
    CurrencyPipe, FPAPipe
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './payment-gateway.component.html',
  styleUrl: './payment-gateway.component.scss'
})
export class PaymentGatewayComponent {

  hasWebSiteBuilder: boolean = false;
  deal: Deal | null = null;
  currentDueAmount: number = 0;
  actualDueAmount: number = 0;

  customerMasterData: CustomerMasterData = { enterpriseTypes: [], localities: [], operatingPeriods: [], venueTiers: [], venueTypes: [] };
  nextPayment: DealPayment | null = null;
  nextPaymentDate: string = "";
  countries = allCountries;

  @ViewChild(StripePaymentElementComponent)
  paymentElement!: StripePaymentElementComponent;

  stripe: StripeServiceInterface | undefined;
  wizardStep = signal<'' | 'SUBSCRIPTION_PREVIEW' | 'CUSTOMER_DATA' | 'SUBMITING_CUSTOMER_DATA' | 'PAYMENT_DATA' | 'COLLECT_CARD_NUMBER' | 'PAYING' | 'SUCCESS' | 'COLLECTION_SUCCESS' | 'ALREADY_PAID' | 'FAILURE'>('');

  protected readonly paymentElementForm = new FormGroup({
    vat: new FormControl('', [Validators.required, Validators.minLength(9)]),
    name: new FormControl('', [Validators.required]),
    legalName: new FormControl('', [Validators.required]),
    phone: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    officialEmail: new FormControl('', [Validators.required, Validators.email]),
    country: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required]),
    address: new FormControl('', [Validators.required]),
    streetNumber: new FormControl('', [Validators.required]),
    zipcode: new FormControl('', [Validators.required]),

    locality: new FormControl('', []),
    operatingPeriod: new FormControl('', [Validators.required]),
    venueTier: new FormControl('', [Validators.required]),
    venueType: new FormControl('', [Validators.required]),
  });

  protected paymentElementFormTriedToSubmit = false;

  get vat() { return this.paymentElementForm.get('vat'); }
  get name() { return this.paymentElementForm.get('name'); }
  get legalName() { return this.paymentElementForm.get('legalName'); }  //new 
  get phone() { return this.paymentElementForm.get('phone'); }  //new 
  get streetNumber() { return this.paymentElementForm.get('streetNumber'); }  //new 
  get email() { return this.paymentElementForm.get('email'); }
  get officialEmail() { return this.paymentElementForm.get('officialEmail'); }
  get address() { return this.paymentElementForm.get('address'); }
  get zipcode() { return this.paymentElementForm.get('zipcode'); }
  get city() { return this.paymentElementForm.get('city'); }
  get country() { return this.paymentElementForm.get('country'); }

  get locality() { return this.paymentElementForm.get('locality'); }
  get operatingPeriod() { return this.paymentElementForm.get('operatingPeriod'); }
  get venueTier() { return this.paymentElementForm.get('venueTier'); }
  get venueType() { return this.paymentElementForm.get('venueType'); }

  private paymentRef = "";

  private getStripeLang(): StripeElementLocale {
    switch (this.translateService.currentLang) {
      case 'en':
        return 'en';
      case 'hr':
        return 'hr';
    }

    return 'el';
  }

  elementsOptions: StripeElementsOptions = {
    locale: this.getStripeLang()
  };

  paymentElementOptions: StripePaymentElementOptions = {
    layout: {
      type: 'tabs',
      defaultCollapsed: false,
      radios: false,
      spacedAccordionItems: false
    }
  };

  constructor(
    private activatedRoute: ActivatedRoute,
    private billingService: BillingServiceService,
    private confirmationService: ConfirmationService,
    protected translateService: TranslateService,
    private loadingService: LoadingService) {

    //this.translateService.use('en');

    this.translateService.onLangChange.subscribe(() => {
      this.elementsOptions.locale = this.getStripeLang();
    });

    this.activatedRoute.params.subscribe(async params => {
      this.paymentRef = params["ref"];

      this.billingService.getOffer(this.paymentRef).subscribe(deal => {
        this.deal = deal;
        this.hasWebSiteBuilder = deal.lines.find(l => l.crmId == 280) != null;
        this.currentDueAmount = (deal?.payments?.find(p => p.index == deal?.currentInstalment)?.amount) ?? 0;
        this.wizardStep.set(
          deal.payments.length == 0 ||
            deal.payments.find(p => p.done != true) == null ||
            deal.payments.find(p => p.isProcessing == true) != null
            ? 'ALREADY_PAID' : 'SUBSCRIPTION_PREVIEW');
        this.paymentElementForm.patchValue({
          vat: this.deal?.customer?.vatNumber ?? '',
          name: this.deal?.customer?.name ?? '',
          legalName: this.deal?.customer?.legalName ?? '',
          phone: this.deal?.customer?.phone ?? '',
          officialEmail: this.deal?.customer?.officialEmail ?? '',
          email: this.deal?.customer?.email ?? '',
          address: this.deal?.customer?.address ?? '',
          zipcode: this.deal?.customer?.zipCode ?? '',
          city: this.deal?.customer?.city ?? '',
          streetNumber: this.deal?.customer?.streetNumber ?? '',
          country: this.deal?.customer?.country ?? '',

          locality: this.deal?.customer?.locality ?? '',
          operatingPeriod: this.deal?.customer?.operatingPeriod ?? '',
          venueTier: this.deal?.customer?.venueTier ?? '',
          venueType: this.deal?.customer?.venueType ?? '',
        });

        //this.nextPayment = { amount: 0, done: false, datePaid: "", dueDate: "", isProcessing: true, index: 1 };
        //this.wizardStep.set('SUCCESS');
      });

      this.billingService.getCustomerMasterData().subscribe(data => {
        this.customerMasterData = { ...data };
      });
    });

    this.stripe = injectStripe(environment.stripeKey);
  }

  proceedToCustomerData() {
    if (this.deal?.type == DealType.OVERDUE) {
      this.wizardStep.set('PAYMENT_DATA');

      this.billingService.getPaymentIntent(this.paymentRef).subscribe(key => {
        this.elementsOptions.clientSecret = key.clientSecret;
        this.actualDueAmount = key.amount;
      });
    } else {
      this.wizardStep.set('CUSTOMER_DATA');
    }
  }

  backToPreview() {
    this.wizardStep.set('SUBSCRIPTION_PREVIEW');
  }

  backToCustomerData() {
    if (this.deal?.type == DealType.OVERDUE) {
      this.backToPreview();
    } else {
      this.wizardStep.set('CUSTOMER_DATA');
    }
  }

  async submitCustomerData() {
    this.paymentElementFormTriedToSubmit = true;
    if (this.paymentElementForm.invalid) {
      return;
    }
    this.wizardStep.set('SUBMITING_CUSTOMER_DATA');

    const data = this.paymentElementForm.getRawValue();

    let customerPatch: Customer = {
      vatNumber: data.vat ?? '',
      address: data.address ?? '',
      city: data.city ?? '',
      country: data.country ?? '',
      email: data.email ?? '',
      officialEmail: data.officialEmail ?? '',
      name: data.name ?? '',
      legalName: data.legalName ?? '',
      phone: data.phone ?? '',
      zipCode: data.zipcode ?? '',
      streetNumber: data.streetNumber ?? '',
      taxPolicy: CustomerTaxCategory.UNSPECIFIED,

      locality: data.locality ?? undefined,
      operatingPeriod: data.operatingPeriod ?? undefined,
      venueTier: data.venueTier ?? undefined,
      venueType: data.venueType ?? undefined,
    }

    this.billingService.updateCustomerData(this.paymentRef, customerPatch).subscribe(res => {
      if (this.deal?.netValue == 0) {
        this.wizardStep.set('COLLECT_CARD_NUMBER');

        this.billingService.getSetupIntent(this.paymentRef).subscribe(key => {
          this.elementsOptions.clientSecret = key.clientSecret;
          this.actualDueAmount = 0;
        });
      } else {
        this.wizardStep.set('PAYMENT_DATA');

        this.billingService.getPaymentIntent(this.paymentRef).subscribe(key => {
          this.elementsOptions.clientSecret = key.clientSecret;
          this.actualDueAmount = key.amount;
        });
      }
    });
  }

  private isPaying = false;

  collectPaymentData() {
    if (this.wizardStep() != 'COLLECT_CARD_NUMBER' ||
      this.isPaying) {
      return;
    }

    this.loadingService.setLoading(true);
    this.isPaying = true;

    const {
      name,
      email,
      address,
      zipcode,
      city,
      country
    } = this.paymentElementForm.getRawValue();

    this.stripe!
      .confirmSetup({
        elements: this.paymentElement.elements,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: name as string,
              email: this.fixEmail(email as string),
              address: {
                line1: address as string,
                postal_code: zipcode as string,
                city: city as string,
                country: country as string
              }
            }
          }
        },
        redirect: 'if_required'
      }).subscribe(result => {
        this.loadingService.setLoading(false);
        console.log(result);

        if (result.error != null) {
          this.isPaying = false;
          this.showErrorMessage(this.translateService.instant("result.error.title"), result.error.message ?? '');
        } else {
          this.billingService.setupCompleted(result.setupIntent.id).subscribe((data) => {
            this.setupCompleted();
          });
        }
      });
  }

  pay() {
    if (this.wizardStep() != 'PAYMENT_DATA' ||
      (this.deal?.type == DealType.OVERDUE ? false : this.paymentElementForm.invalid) ||
      this.isPaying
    ) {
      return;
    }

    this.isPaying = true;

    this.loadingService.setLoading(true);
    this.isPaying = true;

    const {
      name,
      email,
      address,
      zipcode,
      city,
      country
    } = this.paymentElementForm.getRawValue();

    this.stripe!
      .confirmPayment({
        elements: this.paymentElement.elements,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: name as string,
              email: this.fixEmail(email as string),
              address: {
                line1: address as string,
                postal_code: zipcode as string,
                city: city as string,
                country: country as string
              }
            }
          }
        },
        redirect: 'if_required'
      })
      .subscribe(result => {
        this.loadingService.setLoading(false);
        console.log(result);

        if (result.error) {
          this.isPaying = false;
          this.showErrorMessage(this.translateService.instant("result.error.title"), result.error.message ?? '');
        } else {
          this.billingService.paymentCompleted(result.paymentIntent.id).subscribe((data) => {
            this.paymentCompleted(data.nextPayment);
          });
        }
      });
  }

  private fixEmail(email: string) {
    try {
      email = email ?? "";
      if (email.indexOf(",") > -1) {
        email = email.split(",")[0];
      }
      if (email.indexOf(";") > -1) {
        email = email.split(";")[0];
      }
      return email.trim();
    }
    catch (e) {
      return email;
    }
  }

  private showErrorMessage(title: string, message: string) {
    this.confirmationService.confirm({
      header: title,
      message: message,
      accept: () => {

      },
      reject: () => {
      }
    });
  }

  private paymentCompleted(nextPayment: DealPayment) {
    this.nextPayment = nextPayment;
    if (this.nextPayment != null) {
      this.nextPaymentDate = new Date(this.nextPayment.dueDate).toLocaleDateString(this.translateService.currentLang, { year: 'numeric', month: 'long', day: 'numeric' });
      this.wizardStep.set('SUCCESS');
    } else {
      this.wizardStep.set('FAILURE');
    }
  }

  private setupCompleted() {
    this.wizardStep.set('COLLECTION_SUCCESS');
  }

  close() {
    window.location.href = "https://www.i-host.gr/";
  }
}
